// The client-facing view of a meeting (spec §4.3, D12). The presenter window — and, in phase 6,
// the email — may render ONLY this. It drops internal data (sources, review flags, edit stamps,
// send/status history) and every SharePoint/OneDrive link: those never leave Sawtooth.
// Every host regex is written "(^|\.)host$" so an optional subdomain (or none) is accepted, but
// the string must genuinely END in that host — a decoy like "notsharepoint.com.evil.io" doesn't
// end in "sharepoint.com" and is correctly rejected.
import { mapInlineLinks } from "./markdownLite.js";

const SP_HOSTS = [
  /(^|\.)sharepoint\.com$/i,
  /(^|\.)sharepoint\.us$/i,
  /(^|\.)sharepoint-mil\.us$/i,
  /(^|\.)onedrive\.live\.com$/i,
  /(^|\.)1drv\.ms$/i,
  /(^|\.)sharepoint\.de$/i,
  /(^|\.)sharepoint\.cn$/i,
  /(^|\.)docs\.live\.net$/i,
];
// A markdown link's target, optionally followed by a quoted title: [text](url "title") / (url 'title').
const MD_LINK = /\[([^\]]+)\]\((\S+?)(?:\s+(?:"[^"]*"|'[^']*'))?\)/g;
const BARE_URL = /https?:\/\/[^\s)\]]+/gi;
// A host-first reference with no scheme, e.g. "sawtooth.sharepoint.com/x", "//a.sharepoint.com/x",
// or "www.1drv.ms/y" — an optional "//" prefix, a dotted domain, and an optional path. The
// lookbehind refuses a match immediately preceded by "@", a word character, "." or "-", so it
// never fires on an email address's domain (e.g. "me@sawtooth.sharepoint.com").
const HOST_FIRST = /(?<![@\w.-])(?:\/\/)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s)\]]*)?/gi;
// Last resort when the passes don't settle: drop every link of any kind (fail closed).
const ANY_MD_LINK = /\[([^\]]+)\]\((\S+?)(?:\s+(?:"[^"]*"|'[^']*'))?\)/g;

function isSharePointHost(host) {
  const h = String(host || "").replace(/\.$/, ""); // trailing-dot FQDN notation
  return SP_HOSTS.some((re) => re.test(h));
}

export function isSharePointUrl(url) {
  try { return isSharePointHost(new URL(url).hostname); } catch { return false; }
}

function stripOnce(text) {
  const t = text
    .replace(MD_LINK, (m, label, url) => (isSharePointUrl(url) ? label : m))
    .replace(BARE_URL, (url) => (isSharePointUrl(url) ? "(SharePoint link removed)" : url))
    .replace(HOST_FIRST, (m) => {
      const host = m.replace(/^\/\//, "").split("/")[0];
      return isSharePointHost(host) ? "(SharePoint link removed)" : m;
    });
  // Structural pass: the regex passes above can be out-tokenized by crafted text (e.g. a link
  // hidden in another link's quoted title), so finally unwrap any SharePoint link exactly as
  // MarkdownLite would tokenize it.
  return mapInlineLinks(t, (label, href, m) => (isSharePointUrl(href) ? label : m));
}

export function stripAllLinks(text) {
  let t = text;
  for (let i = 0; i < 50; i += 1) {
    const next = t.replace(ANY_MD_LINK, (m, label) => label);
    if (next === t) break;
    t = next;
  }
  t = t.replace(BARE_URL, "(link removed)").replace(HOST_FIRST, (m) => (/[a-z]{2,}\//i.test(m) || /sharepoint|onedrive|1drv|docs\.live/i.test(m) ? "(link removed)" : m));
  return /sharepoint|onedrive|1drv\.ms|docs\.live\.net/i.test(t) ? "(content removed)" : t;
}

export function stripSharePointLinks(text) {
  // Each pass can expose a new link (an unwrapped one may complete another), so repeat until the
  // text stops changing. If it still hasn't settled after 10 passes, fail closed.
  let t = String(text ?? "");
  for (let i = 0; i < 10; i += 1) {
    const next = stripOnce(t);
    if (next === t) return t;
    t = next;
  }
  return stripAllLinks(t);
}

export function hasSharePointLink(text) {
  const t = String(text ?? "");
  return stripSharePointLinks(t) !== t;
}

export function publicView(meeting) {
  const m = meeting || {};
  return {
    id: m.id, clientId: m.clientId, projectId: m.projectId ?? null, type: m.type, number: m.number,
    meetingDate: m.meetingDate, periodStart: m.periodStart, periodEnd: m.periodEnd, status: m.status,
    summary: { headline: stripSharePointLinks(m.summary?.headline), hiddenTiles: [...(m.summary?.hiddenTiles || [])], rev: m.summary?.rev ?? 0 },
    loe: { hiddenObjectiveIds: [...(m.loe?.hiddenObjectiveIds || [])], rev: m.loe?.rev ?? 0 },
    crm: { hidden: m.crm?.hidden === true, hiddenEventIds: [...(m.crm?.hiddenEventIds || [])], rev: m.crm?.rev ?? 0 },
    sections: (m.sections || []).map((s) => ({
      id: s.id, title: stripSharePointLinks(s.title), kind: s.kind, rev: s.rev ?? 0,
      items: (s.items || []).map((i) => ({
        id: i.id,
        headline: stripSharePointLinks(i.headline),
        body: stripSharePointLinks(i.body),
        checked: i.checked === true,
        decisionNeeded: i.decisionNeeded === true,
        owner: i.owner ? { ...i.owner } : null,
        dueDate: i.dueDate ?? null,
        needsReview: false,
        sources: [],
      })),
    })),
  };
}

// Lines of effort (objective + task names) come from raw project data, not the meeting doc, so the
// presenter runs its computeLoe rows through this before rendering them.
export function publicLoeRows(rows) {
  return (rows || []).map((r) => ({
    ...r,
    name: stripSharePointLinks(r.name),
    doneInPeriod: (r.doneInPeriod || []).map((n) => stripSharePointLinks(n)),
    upNext: (r.upNext || []).map((u) => ({ ...u, name: stripSharePointLinks(u.name) })),
  }));
}
